import type { SurveyPaginationRefs } from '../types';

/**
 * Records a forward move (Next or skip jump) in `pageHistory`.
 *
 * If the user previously went Back and is moving forward again from an earlier
 * page, any stale "future" tail is dropped so Back always follows the path they
 * actually took — not `currentPageIndex - 1`.
 */
export function pushPageHistory(
  refs: SurveyPaginationRefs,
  fromPageIndex: number,
  toPageIndex: number
): void {
  const history = refs.pageHistory;
  const fromPosition = history.lastIndexOf(fromPageIndex);

  refs.pageHistory =
    fromPosition >= 0
      ? [...history.slice(0, fromPosition + 1), toPageIndex]
      : [...history, fromPageIndex, toPageIndex];
}

/**
 * Undoes the last forward move. Returns the page to show, or null when already
 * at the start of the survey (history is only `[0]`).
 */
export function popPageHistory(refs: SurveyPaginationRefs): number | null {
  if (refs.pageHistory.length <= 1) {
    return null;
  }

  refs.pageHistory = refs.pageHistory.slice(0, -1);
  return refs.pageHistory.at(-1) ?? null;
}

/** Page indices that were not skipped over via skip logic (used at submit validation). */
export function getNonSkippedPageIndices(
  pageCount: number,
  skippedPageIndices: ReadonlySet<number>
): number[] {
  const indices: number[] = [];

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    if (!skippedPageIndices.has(pageIndex)) {
      indices.push(pageIndex);
    }
  }

  return indices;
}
